"use client";

import clsx from "clsx";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { composeRefs } from "../../../lib/composeRefs";
import { useDebouncedCallback } from "../../../lib/useDebouncedCallback";
import type { InputProps } from "../InputTypes";
import { textInputClassName } from "../TextInput/TextInput";

const styles = { control: textInputClassName };

export type TextAreaProps = React.ComponentProps<"textarea"> &
  InputProps & {
    onValueChange?: (value: any) => void;
    ref?: React.Ref<HTMLTextAreaElement>;
  };

const TextArea = (props: TextAreaProps) => {
  const {
    className,
    onValueProcess,
    autoFocus,
    onBlur,
    ref,
    onChange,
    onValueChange,
    ...otherProps
  } = props;
  const ownInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) {
      ownInputRef.current?.focus();
    }
  }, [autoFocus]);

  const [valid, setValid] = useState<boolean>(true);

  const checkValidity = useDebouncedCallback(
    () => {
      setValid(ownInputRef.current?.checkValidity() ?? true);
    },
    [],
    500,
  );

  return (
    <textarea
      onBlur={(e) => {
        onBlur?.(e);
        checkValidity.immediate();
      }}
      ref={composeRefs(ref, ownInputRef)}
      className={clsx(styles.control, className)}
      onChange={(e) => {
        let processedValue = e.target.value;
        if (onValueProcess) {
          processedValue = onValueProcess(processedValue);
          e.target.value = processedValue;
        }
        onValueChange?.(processedValue);
        onChange?.(e);
        setValid(true);
        checkValidity();
      }}
      {...otherProps}
      aria-invalid={otherProps["aria-invalid"] ?? (!valid ? true : undefined)}
      data-invalid={otherProps["aria-invalid"] || !valid ? "true" : undefined}
    />
  );
};

export default TextArea;
